import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Typography, Button, Card, CardContent, Box } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { CheckCircle, ErrorOutline } from '@material-ui/icons';
import { green } from '@material-ui/core/colors';
import { SurveyService } from '../../services/surveyService';

type PageStatus = 'confirm' | 'processing' | 'success' | 'error';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(4),
    width: '100%',
    maxWidth: 650,
    margin: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0),
    },
  },
  card: {
    width: '100%',
    maxWidth: 650,
    margin: 'auto',
  },
  content: {
    padding: theme.spacing(4, 2),
    textAlign: 'center',
  },
  text: {
    padding: theme.spacing(4),
    lineHeight: '2',
    margin: '20px 0',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(4, 0),
    },
  },
  successIcon: {
    fontSize: 60,
    color: green[500],
  },
  errorIcon: {
    fontSize: 60,
    color: theme.palette.error.main,
  },
}));

const surveyService = new SurveyService();

export const SurveyUnsubscribe = observer(() => {
  const classes = useStyles();
  const { token } = useParams<{ token: string }>();
  const [pageStatus, setPageStatus] = useState<PageStatus>('confirm');

  const handleUnsubscribe = async () => {
    setPageStatus('processing');
    try {
      const response = await surveyService.unsubscribe(token);
      const body = response.data;
      if (body.status === 'OK') {
        setPageStatus('success');
      } else {
        setPageStatus('error');
      }
    } catch {
      setPageStatus('error');
    }
  };

  return (
    <Grid container spacing={2} justifyContent="center" className={classes.container}>
      <Grid item xs={12}>
        <Card className={classes.card}>
          <CardContent className={classes.content}>
            {pageStatus === 'confirm' && (
              <>
                <Typography className={classes.text}>
                  Unsubscribe from satisfaction survey emails?
                  <br />
                  You won't receive these emails in the future.
                </Typography>
                <Box mb={2}>
                  <Button variant="contained" color="primary" onClick={handleUnsubscribe}>
                    Confirm Unsubscribe
                  </Button>
                </Box>
              </>
            )}

            {pageStatus === 'processing' && (
              <Typography className={classes.text}>
                Processing your request, please wait...
              </Typography>
            )}

            {pageStatus === 'success' && (
              <>
                <Box mt={2} mb={1}>
                  <CheckCircle className={classes.successIcon} />
                </Box>
                <Typography variant="h5" gutterBottom>
                  You've been unsubscribed
                </Typography>
                <Typography className={classes.text}>
                  You won't receive satisfaction survey emails anymore.
                  <br />
                  You can now close this window.
                </Typography>
              </>
            )}

            {pageStatus === 'error' && (
              <>
                <Box mt={2} mb={1}>
                  <ErrorOutline className={classes.errorIcon} />
                </Box>
                <Typography variant="h6" gutterBottom>
                  There was an error processing your request.
                </Typography>
                <Typography color="textSecondary">
                  Please try again or contact your loan officer if the problem persists.
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
});
